import dayjs from "dayjs";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { EventType } from "../types";
import { EventSummary } from "../components/booking/EventSummary";
import { PoweredRibbon } from "../components/booking/PoweredRibbon";
import { buildCalendar } from "../utils/calendar";
import { isValidMongolianPhone } from "../utils/phone";

function getPrefillForm() {
  const params = new URLSearchParams(window.location.search);

  return {
    name: params.get("name") || "",
    email: params.get("email") || "",
    phone: params.get("phone") || "+976",
    quantity: params.get("quantity") || "",
    giftCode: params.get("gift") || "",
    childGender: params.get("childGender") || "",
  };
}

function needsChildGender(event: EventType | null) {
  if (!event) return false;
  const text = `${event.id} ${event.title}`.toLowerCase();
  return text.includes("huuhdiin-tur") || text.includes("хүүхдийн") || text.includes("төр");
}

export function BookingPage({ eventId }: { eventId: string }) {
  const [event, setEvent] = useState<EventType | null>(null);
  const [month, setMonth] = useState(dayjs().startOf("month"));
  const [availability, setAvailability] = useState<Record<string, string[]>>({});
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTime, setSelectedTime] = useState("");
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [form, setForm] = useState(getPrefillForm);
  const [phoneError, setPhoneError] = useState("");
  const [bookingError, setBookingError] = useState("");
  const [bookingSubmitting, setBookingSubmitting] = useState(false);

  useEffect(() => {
    api.getEvent(eventId).then(setEvent);
  }, [eventId]);

  useEffect(() => {
    api.getAvailability(eventId, month.format("YYYY-MM")).then(setAvailability);
  }, [eventId, month]);

  const days = useMemo(() => buildCalendar(month), [month]);
  const slots = selectedDate ? availability[selectedDate] || [] : [];
  const showChildGender = needsChildGender(event);

  async function refreshAvailability() {
    const nextAvailability = await api.getAvailability(eventId, month.format("YYYY-MM"));
    setAvailability(nextAvailability);
    return nextAvailability;
  }

  async function submitBooking(e: FormEvent) {
    e.preventDefault();
    if (!event) return;
    if (!isValidMongolianPhone(form.phone)) {
      setPhoneError("Утасны дугаараа +97680020260 эсвэл 80020260 форматаар оруулна уу.");
      return;
    }
    setPhoneError("");
    setBookingError("");
    setBookingSubmitting(true);

    try {
      const booking = await api.createBooking({
        eventId,
        date: selectedDate,
        time: selectedTime,
        name: form.name,
        email: form.email,
        phone: form.phone,
        note: `quantity=${form.quantity}; gift=${form.giftCode}; childGender=${showChildGender ? form.childGender : ""}`,
      });
      window.location.href = `/payment/${booking.id}`;
    } catch (error) {
      await refreshAvailability();
      setSelectedTime("");
      setDetailsOpen(false);
      setBookingSubmitting(false);
      setBookingError(error instanceof Error ? error.message : "Сонгосон цаг боломжгүй байна. Өөр цаг сонгоно уу.");
    }
  }

  if (!event) return <main className="booking-page">Уншиж байна...</main>;

  return (
    <main className="booking-page">
      <section className={detailsOpen ? "calendly-card details-mode" : "calendly-card"}>
        <PoweredRibbon />
        <EventSummary event={event} selectedDate={selectedDate} selectedTime={selectedTime} />

        {detailsOpen ? (
          <section className="details-panel">
            <h2>Enter Details</h2>
            <form className="details-form" onSubmit={submitBooking}>
              <label>
                Name *
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </label>
              <label>
                Email *
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </label>
              <label>
                Утасны дугаар ? *
                <input
                  required
                  inputMode="tel"
                  value={form.phone}
                  onChange={(e) => {
                    setForm({ ...form, phone: e.target.value });
                    setPhoneError("");
                  }}
                />
                {phoneError && <span className="field-error">{phoneError}</span>}
              </label>
              <label>
                Зураг даруулах хүний тоо *
                <select required value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })}>
                  <option value="">Select...</option>
                  <option>1 хүн</option>
                  <option>2 хүн</option>
                  <option>3 хүн</option>
                  <option>4 хүн</option>
                  <option>5 хүн</option>
                  <option>6 хүн</option>
                </select>
              </label>
              <label>
                Gift card code ? байхгүй бол хоосон орхино уу
                <input value={form.giftCode} onChange={(e) => setForm({ ...form, giftCode: e.target.value })} />
              </label>
              {showChildGender && (
                <label>
                  Хүү / Охин *
                  <select required value={form.childGender} onChange={(e) => setForm({ ...form, childGender: e.target.value })}>
                    <option value="">Сонгох...</option>
                    <option>Хүү</option>
                    <option>Охин</option>
                  </select>
                </label>
              )}
              {bookingError && <div className="booking-error">{bookingError}</div>}
              <button className="primary-action" type="submit" disabled={bookingSubmitting}>
                {bookingSubmitting ? "Захиалга үүсгэж байна..." : "Schedule Event"}
              </button>
            </form>
          </section>
        ) : (
          <>
            <section className="calendar-panel">
              {bookingError && <div className="booking-error calendar-error">{bookingError}</div>}
              <div className="calendar-title">
                <h2>Өдөр цагаа сонгоно уу</h2>
                <div>
                  <button type="button" onClick={() => setMonth(month.subtract(1, "month"))}>
                    ‹
                  </button>
                  <strong>{month.format("MMMM YYYY")}</strong>
                  <button type="button" onClick={() => setMonth(month.add(1, "month"))}>
                    ›
                  </button>
                </div>
              </div>
              <div className="weekday-grid">
                {["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"].map((day) => (
                  <span key={day}>{day}</span>
                ))}
              </div>
              <div className="date-grid">
                {days.map((item) => {
                  const key = item.date.format("YYYY-MM-DD");
                  const hasSlots = (availability[key] || []).length > 0;
                  return (
                    <button
                      className={selectedDate === key ? "selected" : ""}
                      disabled={!item.inMonth || !hasSlots || item.date.isBefore(dayjs(), "day")}
                      key={item.key}
                      type="button"
                      onClick={() => {
                        setSelectedDate(key);
                        setSelectedTime("");
                      }}
                    >
                      {item.date.date()}
                    </button>
                  );
                })}
              </div>
              <div className="timezone">
                <strong>Time zone</strong>
                <span>◉ Ulaanbaatar Time ({dayjs().format("HH:mm")})⌄</span>
              </div>
            </section>

            {selectedDate && (
              <section className="time-panel">
                <h2>{dayjs(selectedDate).format("dddd, MMMM D")}</h2>
                <div className="slots">
                  {slots.map((slot) => (
                    <div className={selectedTime === slot ? "slot-pair selected" : "slot-pair"} key={slot}>
                      <button type="button" onClick={() => setSelectedTime(slot)}>
                        {slot}
                      </button>
                      {selectedTime === slot && (
                        <button className="next-button" type="button" onClick={() => setDetailsOpen(true)}>
                          Next
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </section>
    </main>
  );
}
